import React from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  ArrowBackIos as ArrowBackIcon,
  ArrowForwardIos as ArrowForwardIcon,
  Add as AddIcon,
} from '@mui/icons-material';

const EVENT_TYPE_COLORS = {
  assembly: '#2196F3',
  holiday: '#FF9800',
  exam_period: '#F44336',
  trip: '#4CAF50',
  celebration: '#9C27B0',
  meeting: '#00BCD4',
  workshop: '#FFC107',
  sports_day: '#E91E63',
  graduation: '#795548',
  other: '#9E9E9E',
};

const EVENT_TYPE_LABELS = {
  assembly: 'اجتماع',
  holiday: 'عطلة',
  exam_period: 'فترة امتحانات',
  trip: 'رحلة',
  celebration: 'احتفال',
  meeting: 'اجتماع',
  workshop: 'ورشة عمل',
  sports_day: 'يوم رياضي',
  graduation: 'تخرج',
  other: 'أخرى',
};

const EventCalendarView = ({
  events = [],
  loading = false,
  currentMonth,
  currentYear,
  onMonthChange,
  onEventClick,
  onEventCreate,
}) => {
  const monthNames = [
    'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
    'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
  ];

  const getDaysInMonth = (month, year) => {
    return new Date(year, month, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    const firstDay = new Date(year, month - 1, 1).getDay();
    // JavaScript: Sunday=0, Monday=1, ..., Saturday=6
    // For RTL calendar starting with Sunday (أحد), we keep Sunday=0
    // So we return the day as-is (0-6 where 0=Sunday)
    return firstDay;
  };

  const handlePrevMonth = () => {
    if (currentMonth === 1) {
      onMonthChange(12, currentYear - 1);
    } else {
      onMonthChange(currentMonth - 1, currentYear);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 12) {
      onMonthChange(1, currentYear + 1);
    } else {
      onMonthChange(currentMonth + 1, currentYear);
    }
  };

  const parseLocalDate = (dateStr) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
  };

  const getEventsForDate = (date) => {
    return events.filter(event => {
      const eventStart = parseLocalDate(event.start_date);
      const eventEnd = event.end_date ? parseLocalDate(event.end_date) : eventStart;
      const checkDate = new Date(currentYear, currentMonth - 1, date);

      return checkDate >= eventStart && checkDate <= eventEnd && !event.is_cancelled;
    });
  };

  const daysInMonth = getDaysInMonth(currentMonth, currentYear);
  const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
  const days = [];
  
  // Add empty cells for days before the first day of the month
  for (let i = 0; i < firstDay; i++) {
    days.push(null);
  }
  
  // Add days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    days.push(day);
  }

  const weekDays = ['أحد', 'إثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت'];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Month Navigation */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <IconButton onClick={handlePrevMonth}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          {monthNames[currentMonth - 1]} {currentYear}
        </Typography>
        <IconButton onClick={handleNextMonth}>
          <ArrowForwardIcon />
        </IconButton>
      </Stack>

      {/* Calendar Grid */}
      <Paper sx={{ p: 2 }}>
        <Box dir="rtl">
          {/* Week Days Header */}
          <Box
            sx={{
              display: 'flex',
              mb: 1,
            }}
          >
            {weekDays.map((day, index) => (
              <Box
                key={index}
                sx={{
                  flex: 1,
                  textAlign: 'center',
                  py: 1,
                  fontWeight: 'bold',
                  color: 'text.secondary',
                }}
              >
                {day}
              </Box>
            ))}
          </Box>

          {/* Calendar Days Grid */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            {days.map((day, index) => {
              if (day === null) {
                return (
                  <Box
                    key={`empty-${index}`}
                    sx={{
                      flex: '1 1 calc(14.285% - 8px)',
                      minWidth: 'calc(14.285% - 8px)',
                      minHeight: 100,
                    }}
                  />
                );
              }

              const dayEvents = getEventsForDate(day);
              const isToday =
                day === new Date().getDate() &&
                currentMonth === new Date().getMonth() + 1 &&
                currentYear === new Date().getFullYear();

              return (
                <Box
                  key={day}
                  sx={{
                    flex: '1 1 calc(14.285% - 8px)',
                    minWidth: 'calc(14.285% - 8px)',
                  }}
                >
                <Paper
                  elevation={isToday ? 3 : 0}
                  sx={{
                    minHeight: 100,
                    p: 1,
                    border: isToday ? 2 : 1,
                    borderColor: isToday ? 'primary.main' : 'divider',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                  onClick={() => onEventCreate(new Date(currentYear, currentMonth - 1, day))}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: isToday ? 'bold' : 'normal',
                        color: isToday ? 'primary.main' : 'text.primary',
                      }}
                    >
                      {day}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEventCreate(new Date(currentYear, currentMonth - 1, day));
                      }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                  <Stack spacing={0.5}>
                    {dayEvents.slice(0, 3).map((event) => (
                      <Chip
                        key={event.id}
                        label={event.title}
                        size="small"
                        sx={{
                          bgcolor: EVENT_TYPE_COLORS[event.event_type] || EVENT_TYPE_COLORS.other,
                          color: 'white',
                          fontSize: '0.7rem',
                          height: 20,
                          cursor: 'pointer',
                          '&:hover': {
                            opacity: 0.8,
                          },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEventClick(event.id);
                        }}
                      />
                    ))}
                    {dayEvents.length > 3 && (
                      <Typography variant="caption" color="text.secondary">
                        +{dayEvents.length - 3} أكثر
                      </Typography>
                    )}
                  </Stack>
                </Paper>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Paper>

      {/* Legend */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          أنواع الفعاليات:
        </Typography>
        <Stack useFlexGap direction="row" spacing={1} flexWrap="wrap">
          {Object.entries(EVENT_TYPE_COLORS).map(([type, color]) => (
            <Chip
              key={type}
              label={EVENT_TYPE_LABELS[type] || type}
              size="small"
              sx={{ bgcolor: color, color: 'white' }}
            />
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

export default EventCalendarView;
